import { useEffect, useState } from 'react';
import {
  Container,
  Group,
  Burger,
  Paper,
  Transition,
  Modal,
  Text,
  Button,
  Divider,
  Stack,
  rem,
} from '@mantine/core';
import React from 'react';
import { createStyles } from '@mantine/emotion';
import { LocaleSwitcher } from './LocaleSwitcher';
import { t } from 'ttag';

const HEADER_HEIGHT = '80px';

const useStyles = createStyles((theme, p, u) => ({
  root: {
    width: '100%',
    position: 'fixed',
    zIndex: 100,
    backgroundColor: 'transparent',
    borderBottom: 0,
    height: HEADER_HEIGHT,
  },

  background: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    backgroundColor: theme.colors.gray[1],
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',

    '&.scrolled': {
      opacity: 1,

      borderBottom: '1px solid #ccc',
    },
  },

  dropdown: {
    position: 'fixed',
    top: HEADER_HEIGHT,
    left: 0,
    right: 0,
    zIndex: 101,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderTopWidth: 0,
    overflow: 'hidden',

    [u.largerThan('md')]: {
      display: 'none',
    },
  },

  header: {
    position: 'relative',
    backgroundColor: 'transparent',
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    height: '100%',

    [u.largerThan('md')]: {
      paddingRight: 40,
    },
  },

  links: {
    [u.smallerThan('md')]: {
      display: 'none',
    },
  },

  burger: {
    [u.largerThan('md')]: {
      display: 'none',
    },
  },

  link: {
    display: 'block',
    lineHeight: 1,
    padding: `${rem(8)} ${rem(12)}`,
    textDecoration: 'none',
    color: theme.colors.gray[0],
    fontSize: theme.fontSizes.md,
    fontWeight: 700,
    letterSpacing: '1px',
    '&.scrolled': {
      color: theme.colors.gray[8],
      '&:hover': {
        color: theme.colors.blue[7],
      },
    },

    '&:hover': {
      color: theme.colors.gray[0],

      [u.smallerThan('md')]: {
        color: theme.colors.blue[6],
      },
    },

    [u.smallerThan('md')]: {
      borderRadius: 0,
      padding: theme.spacing.md,
      color: theme.colors.gray[9],
      fontSize: theme.fontSizes.sm,
    },
  },

  linkActive: {
    '&, &:hover': {
      color: theme.colors.gray[0],
    },

    '&.scrolled': {
      color: theme.colors.gray[7],
    },

    [u.smallerThan('md')]: {
      color: theme.colors.blue[6],
    },
  },
}));

export interface LinkItem {
  link: string;
  label: string;
  newTab: boolean;
  isLoginModal?: boolean;
  eventLink?: string;
}

export interface PageHeaderLinks {
  links: LinkItem[];
}

export const PageHeader: React.FC<PageHeaderLinks> = ({ links }) => {
  const [opened, setOpened] = useState(false);
  const [active, setActive] = useState(links[0]?.link);
  const [loginModalOpened, setLoginModalOpened] = useState(false);

  const { classes, cx } = useStyles();

  const [isScrolled, setIsScrolled] = useState(false);

  const handleScroll = () => {
    const scrolled = window.scrollY > 20;
    setIsScrolled(scrolled);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const loginLink = links.find((l) => l.isLoginModal);

  const items = links.map((link) => (
    <a
      key={link.label}
      href={link.isLoginModal ? undefined : link.link}
      target={link.newTab && !link.isLoginModal ? '_blank' : '_self'}
      rel="noreferrer"
      className={cx(classes.link, {
        [classes.linkActive]: active === link.label,
        ['scrolled']: isScrolled,
      })}
      style={link.isLoginModal ? { cursor: 'pointer' } : undefined}
      onClick={(event) => {
        if (link.isLoginModal) {
          event.preventDefault();
          setLoginModalOpened(true);
        }
        setActive(link.label);
        setOpened(false);
      }}>
      {link.label}
    </a>
  ));

  const imgSrc = isScrolled ? '/grantme_dark_transparent.png' : '/grantme_light_transparent.png';

  return (
    <div className={classes.root}>
      <div className={cx(classes.background, { ['scrolled']: isScrolled })} />
      <Container size={'md'} className={classes.header}>
        <Group justify={'space-between'} w={'100%'}>
          <img
            src={imgSrc}
            height={35}
            onClick={() => (window.location.href = '/')}
            style={{ cursor: 'pointer' }}
          />
          <Group gap={0} justify={'end'} className={classes.links}>
            {items.length > 0 && <Group gap={5}>{items}</Group>}
            <LocaleSwitcher />
          </Group>
        </Group>

        <div className={classes.burger} style={{ paddingRight: items.length > 0 ? 10 : 0 }}>
          <LocaleSwitcher />
        </div>

        {items.length > 0 && (
          <>
            <Burger
              color={isScrolled ? 'black' : 'white'}
              opened={opened}
              onClick={() => setOpened(!opened)}
              className={classes.burger}
              size="md"
            />

            <Transition transition="pop-top-right" duration={200} mounted={opened}>
              {(styles) => (
                <Paper className={classes.dropdown} withBorder style={styles}>
                  {items}
                </Paper>
              )}
            </Transition>
          </>
        )}
      </Container>

      {loginLink && (
        <Modal
          opened={loginModalOpened}
          onClose={() => setLoginModalOpened(false)}
          title={<Text size={'lg'} fw={'bold'}>{t`Logga in`}</Text>}
          centered
          size="md"
          overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}>
          <Stack gap="lg">
            <Stack gap="xs">
              <Text fw={600}>Grantme</Text>
              <Text size="sm" c="gray.7" lh={1.6}>
                {t`För administratörer, beslutsfattare och granskare av ansökningar.`}
              </Text>
              <Button
                component="a"
                href={loginLink.link}
                target="_blank"
                rel="noreferrer"
                fullWidth>
                {t`Logga in på Grantme`}
              </Button>
            </Stack>
            {loginLink.eventLink && (
              <Stack gap="xs">
                <Text fw={600}>Grantme Event</Text>
                <Text size="sm" c="gray.7" lh={1.6}>
                  {t`För dig som administrerar evenemang och inbjudningar.`}
                </Text>
                <Button
                  component="a"
                  href={loginLink.eventLink}
                  target="_blank"
                  rel="noreferrer"
                  fullWidth>
                  {t`Logga in på Grantme Event`}
                </Button>
              </Stack>
            )}
            <Divider />
            <Text size="xs" c="gray.7" lh={1.6}>
              {t`Söker du bidrag? Logga in där du skapade eller ska lämna in ansökan – oftast på organisationens eller stiftelsens hemsida.`}
            </Text>
          </Stack>
        </Modal>
      )}
    </div>
  );
};
